import { Asset } from "expo-asset";
import { Calendar, Users } from "lucide-react-native";
import { FC } from "react";

import jarBalanced from "@/assets/images/launch-simulator/jar-balanced.png";
import {
  Badge,
  BadgeText,
  Box,
  Card,
  HStack,
  Media,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useDatetimeUtils } from "@/hooks";

/* ── Types ─────────────────────────────────────── */

interface SimulationCardProps {
  createdAt: string;
  description: string;
  name: string;
  onPress: () => void;
  questionCount: number;
  respondentCount: number;
}

/* ── Constants ─────────────────────────────────── */

const COVER_URI = Asset.fromModule(jarBalanced).uri;

/* ── Component ─────────────────────────────────── */

/** Tile for one launch simulation, laid out like a `StudyCard`. */
const SimulationCard: FC<SimulationCardProps> = ({
  createdAt,
  description,
  name,
  onPress,
  questionCount,
  respondentCount,
}) => {
  const { formatDate } = useDatetimeUtils();

  return (
    <Box className="h-full">
      <Pressable className="h-full" onPress={onPress}>
        <Card className="relative border border-outline-200 rounded-2xl p-0 overflow-hidden h-full hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5 transition-all duration-300">
          <VStack className="h-full">
            <Box className="w-full h-32 bg-primary-50 items-center justify-center">
              <Media
                alt={name}
                className="border-0 border-transparent"
                height={112}
                resizeMode="contain"
                source={COVER_URI}
                width={112}
              />
            </Box>

            <VStack className="p-5 flex-1 justify-between">
              <VStack>
                <HStack className="items-center gap-2 mb-2">
                  <Badge
                    action="success"
                    className="px-2 py-0.5 rounded-full"
                    variant="solid"
                  >
                    <BadgeText className="text-2xs font-medium lowercase">
                      completed
                    </BadgeText>
                  </Badge>
                  <Text className="text-xs text-typography-500">
                    {questionCount} questions
                  </Text>
                </HStack>
                <Text className="text-sm font-semibold text-typography-900 mb-1">
                  {name}
                </Text>
                <Text className="text-xs text-typography-500 line-clamp-2 mb-3">
                  {description}
                </Text>
              </VStack>

              <HStack className="items-center gap-3">
                <HStack className="items-center gap-1">
                  <Users
                    className="text-typography-500"
                    size={14}
                    strokeWidth={2}
                  />
                  <Text className="text-xs text-typography-500">
                    {respondentCount.toLocaleString("en-US")}
                  </Text>
                </HStack>
                <HStack className="items-center gap-1">
                  <Calendar
                    className="text-typography-500"
                    size={14}
                    strokeWidth={2}
                  />
                  <Text className="text-xs text-typography-500">
                    {formatDate(createdAt)}
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </VStack>
        </Card>
      </Pressable>
    </Box>
  );
};

export default SimulationCard;
